import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ShieldCheck, Star } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Advertise() {
  const [packages, setPackages] = useState([]);
  const [pubPackages, setPubPackages] = useState([]);
  const [tab, setTab] = useState("advertising");
  const { user } = useAuth();
  const nav = useNavigate();
  useEffect(() => {
    api.get("/packages").then((r) => setPackages(r.data)).catch(() => {});
    api.get("/publishing-packages").then((r) => setPubPackages(r.data)).catch(() => {});
  }, []);

  const start = () => {
    if (!user) return nav("/register");
    if (user.role === "advertiser" || user.role === "admin") return nav("/advertiser");
    return nav("/advertiser");
  };

  const choosePublishing = (key) => {
    if (!user) return nav(`/login?next=${encodeURIComponent(`/publish?package=${key}`)}`);
    nav(`/publish?package=${key}`);
  };

  return (
    <div data-testid="advertise-page">
      <section className="bg-pti-black text-white">
        <div className="pti-container py-16 max-w-3xl">
          <span className="text-[11px] uppercase tracking-widest font-bold text-pti-red">Advertise & Publish With PictureThisInk</span>
          <h1 className="font-display text-4xl sm:text-6xl font-black mt-3 leading-none">Two ways to reach our audience.</h1>
          <p className="text-neutral-300 mt-4 text-lg">Run native advertising that feels like editorial, or publish your own story through our editorial desk. Pay securely and go live after review.</p>
          <button onClick={start} data-testid="advertise-start" className="mt-6 bg-pti-red text-white px-7 py-3 font-bold hover:bg-pti-redhover transition-colors">Start a campaign</button>
        </div>
      </section>

      <section className="pti-container py-14">
        {/* Tabs */}
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-0 border-b-2 border-pti-black" role="tablist">
          <button
            role="tab" aria-selected={tab === "advertising"} data-testid="tab-advertising"
            onClick={() => setTab("advertising")}
            className={`text-sm sm:text-base font-bold uppercase tracking-wider px-5 py-3 border-b-4 -mb-[2px] transition-colors ${tab === "advertising" ? "border-pti-red text-pti-black" : "border-transparent text-gray-400 hover:text-pti-black"}`}>
            Advertising Packages
          </button>
          <button
            role="tab" aria-selected={tab === "publishing"} data-testid="tab-publishing"
            onClick={() => setTab("publishing")}
            className={`text-sm sm:text-base font-bold uppercase tracking-wider px-5 py-3 border-b-4 -mb-[2px] transition-colors ${tab === "publishing" ? "border-pti-red text-pti-black" : "border-transparent text-gray-400 hover:text-pti-black"}`}>
            Publishing Packages
          </button>
        </div>

        {/* Advertising packages (existing — unchanged) */}
        {tab === "advertising" && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8" data-testid="advertising-grid">
            {packages.map((p) => (
              <div key={p.lookup_key} data-testid={`pkg-card-${p.lookup_key}`} className="border border-gray-200 p-6 flex flex-col hover:border-pti-black transition-colors">
                <div className="font-display text-xl font-bold">{p.name}</div>
                <div className="font-display text-4xl font-black text-pti-red mt-2">${p.price}<span className="text-base text-gray-400 font-sans font-normal">{p.interval ? "/mo" : ""}</span></div>
                <p className="text-sm text-gray-600 mt-2">{p.tagline}</p>
                <ul className="mt-4 space-y-2 text-sm flex-1">
                  <li className="flex gap-2"><Check size={16} className="text-pti-red shrink-0 mt-0.5" /> {p.placement}</li>
                  <li className="flex gap-2"><Check size={16} className="text-pti-red shrink-0 mt-0.5" /> {p.interval ? "Billed monthly, cancel anytime" : `Runs for ${p.duration_days} days`}</li>
                  <li className="flex gap-2"><Check size={16} className="text-pti-red shrink-0 mt-0.5" /> Category-matched targeting</li>
                  <li className="flex gap-2"><Check size={16} className="text-pti-red shrink-0 mt-0.5" /> Live preview + performance metrics</li>
                </ul>
                <button onClick={start} data-testid={`choose-${p.lookup_key}`} className="mt-5 bg-pti-black text-white py-2.5 font-bold hover:bg-pti-red transition-colors">Choose {p.name}</button>
              </div>
            ))}
          </div>
        )}

        {/* Publishing packages (new) */}
        {tab === "publishing" && (
          <>
            <p className="text-gray-600 mt-6 max-w-2xl">Publish your own article through the PictureThisInk editorial desk. Every submission is reviewed by our team before it goes live. Payment does not guarantee editorial approval — see our policies before checkout.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8" data-testid="publishing-grid">
              {pubPackages.map((p) => (
                <div key={p.lookup_key} data-testid={`pub-card-${p.lookup_key}`}
                  className={`relative border p-6 flex flex-col transition-colors ${p.popular ? "border-pti-red border-2 shadow-sm" : "border-gray-200 hover:border-pti-black"}`}>
                  {p.popular && (
                    <span data-testid="pub-popular-badge" className="absolute -top-3 left-6 bg-pti-red text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1 flex items-center gap-1">
                      <Star size={12} fill="#fff" /> Most Popular
                    </span>
                  )}
                  <div className="font-display text-xl font-bold mt-1">{p.name}</div>
                  <div className="font-display text-4xl font-black text-pti-red mt-2">${p.price}</div>
                  <p className="text-sm text-gray-600 mt-2">{p.tagline}</p>
                  <ul className="mt-4 space-y-2 text-sm flex-1">
                    {p.features.map((f) => (
                      <li key={f} className="flex gap-2"><Check size={16} className="text-pti-red shrink-0 mt-0.5" /> {f}</li>
                    ))}
                  </ul>
                  <button onClick={() => choosePublishing(p.lookup_key)} data-testid={`choose-pub-${p.lookup_key}`}
                    className="mt-5 bg-pti-black text-white py-2.5 font-bold hover:bg-pti-red transition-colors">Choose Package</button>
                </div>
              ))}
            </div>
            <div className="bg-gray-50 border border-gray-200 p-6 mt-8 text-sm text-gray-600" data-testid="publishing-policies">
              <h3 className="font-display text-lg font-bold text-pti-black">Editorial, revision & refund policies</h3>
              <ul className="mt-3 space-y-1.5 list-disc pl-5">
                <li>All articles must comply with PictureThisInk editorial standards.</li>
                <li>Sponsored content is clearly disclosed, and paid links use <code>rel="sponsored"</code>.</li>
                <li><strong>Payment does not guarantee editorial approval.</strong> Our editors may request revisions or decline a submission.</li>
                <li>If a submission is declined and cannot be revised to meet our standards, your payment is refunded in full.</li>
              </ul>
            </div>
          </>
        )}
      </section>

      <section className="pti-container pb-16">
        <div className="bg-gray-50 border border-gray-200 p-8 flex flex-col md:flex-row gap-6 items-start">
          <ShieldCheck size={40} className="text-pti-red shrink-0" />
          <div>
            <h3 className="font-display text-2xl font-bold">Brand-safe by design</h3>
            <p className="text-gray-600 mt-2">Every placement is clearly labeled <strong>Advertisement</strong> or <strong>Sponsored</strong>, reviewed by our team, and matched to relevant categories. We don't accept third-party ad scripts, affiliate code, or adult, gambling, or deceptive content.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
